import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Info, History } from 'lucide-react';
import { api, getCurrentUser } from '../api';
import { ROLE_KO, parseUserAgent, pathToScreen, utcToKst } from '../labels';
import { SkeletonTable } from '../components/Skeleton';

type Session = {
  user_id: number;
  username: string | null;
  name: string | null;
  position: string | null;
  role: 'admin' | 'staff' | null;
  email: string | null;
  active: number | null;
  last_seen_at: string;
  ip: string | null;
  user_agent: string | null;
  last_path: string | null;
  minutes_since: number;
};

const WINDOW_OPTIONS = [5, 10, 30, 60, 180, 720];

export default function SecuritySessions() {
  const [within, setWithin] = useState<number>(10);
  const [data, setData] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [uaDetail, setUaDetail] = useState<number | null>(null);
  const currentUserId = useMemo(() => getCurrentUser()?.id ?? null, []);
  const navigate = useNavigate();

  const load = async (w = within) => {
    setLoading(true);
    try {
      const r = await api<{ data: Session[]; within_minutes: number }>('GET', `/security/sessions?within_minutes=${w}`);
      setData(r.data ?? []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(within); /* eslint-disable-next-line */ }, [within]);

  return (
    <div>
      <h2>최근 활동 계정</h2>
      <div className="card">
        <div className="callout" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
          <Info size={16} style={{ color: 'var(--brand-700)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12.5, lineHeight: 1.55 }}>
            <b>최근 활동 계정 안내</b>: 실제 접속 여부가 아니라 선택한 시간(<b>최근 {within}분</b>) 동안 사이트에서 활동한 계정을 보여줍니다.
            같은 계정이 여러 기기나 브라우저에서 사용된 경우 가장 최근 활동정보가 표시됩니다.
          </div>
        </div>

        <div className="toolbar">
          <div>
            <label>활동 기준(분)</label>
            <select value={within} onChange={e => setWithin(Number(e.target.value))}>
              {WINDOW_OPTIONS.map(w => <option key={w} value={w}>최근 {w}분</option>)}
            </select>
          </div>
          <button className="btn secondary" onClick={() => load(within)}>새로고침</button>
          <div className="muted" style={{ marginLeft: 'auto', fontSize: 13 }}>
            총 <b style={{ color: 'var(--leaf-800)' }}>{data.length}</b>개 계정 활동 (기준: {within}분)
          </div>
        </div>

        {loading ? <SkeletonTable rows={6} cols={7} showHeader /> : data.length === 0 ? (
          <div className="empty-state">
            <ShieldCheck className="empty-icon" size={36} strokeWidth={1.6} />
            <h4>활동중인 계정 없음</h4>
            <p>선택한 시간 안에 인증 요청을 보낸 계정이 없습니다.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>사용자</th>
                <th className="nowrap">권한</th>
                <th className="nowrap">마지막 활동 (KST)</th>
                <th className="num nowrap">경과(분)</th>
                <th className="nowrap">IP</th>
                <th>마지막 화면</th>
                <th>접속환경</th>
                <th className="nowrap" style={{ width: 1 }}>활동 상세</th>
              </tr></thead>
              <tbody>
                {data.map(s => {
                  const isMe = s.user_id === currentUserId;
                  const ua = parseUserAgent(s.user_agent);
                  const screen = pathToScreen(s.last_path);
                  return (
                    <>
                      <tr key={s.user_id} style={isMe ? { background: '#f0fdf4' } : undefined}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ fontWeight: 600 }}>{s.name ?? s.username ?? `#${s.user_id}`}</div>
                            {isMe && <span className="chip" style={{ background: '#dcfce7', color: '#166534', fontSize: 10, padding: '1px 6px' }}>현재 사용자</span>}
                          </div>
                          <div className="muted" style={{ fontSize: 12, fontFamily: 'monospace' }}>{s.username ?? '-'}</div>
                        </td>
                        <td className="nowrap">
                          {s.role
                            ? <span className="chip" style={{ background: s.role === 'admin' ? '#e6f4ea' : '#f1f5f3', color: s.role === 'admin' ? 'var(--leaf-800)' : 'var(--text-muted)' }}>
                                {ROLE_KO[s.role] ?? s.role}
                              </span>
                            : <span className="muted">-</span>}
                          {s.active === 0 && <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>(비활성)</span>}
                        </td>
                        <td className="nowrap" style={{ fontFamily: 'monospace' }} title={`서버 저장(UTC): ${s.last_seen_at}`}>{utcToKst(s.last_seen_at)}</td>
                        <td className="num nowrap" style={s.minutes_since <= 2 ? { color: 'var(--leaf-700)', fontWeight: 700 } : undefined}>
                          {s.minutes_since}분 전
                        </td>
                        <td className="nowrap" style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{s.ip ?? '-'}</td>
                        <td>
                          {screen ? (
                            <div>
                              <div style={{ fontSize: 12.5 }}>{screen}</div>
                              <div className="muted" style={{ fontSize: 11, fontFamily: 'monospace' }}>{s.last_path}</div>
                            </div>
                          ) : (
                            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{s.last_path ?? '-'}</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                            <span>{ua.os} · {ua.browser}</span>
                            {s.user_agent && (
                              <button
                                className="filter-ghost"
                                style={{ padding: '1px 6px', fontSize: 10 }}
                                onClick={() => setUaDetail(v => v === s.user_id ? null : s.user_id)}
                              >
                                {uaDetail === s.user_id ? '숨기기' : '상세'}
                              </button>
                            )}
                          </div>
                          {uaDetail === s.user_id && s.user_agent && (
                            <div style={{
                              marginTop: 4, padding: 6, background: '#f8fafc',
                              border: '1px solid var(--border)', borderRadius: 4,
                              fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all',
                            }}>{s.user_agent}</div>
                          )}
                        </td>
                        <td className="nowrap">
                          {s.username && (
                            <button
                              className="filter-ghost"
                              style={{ fontSize: 11, padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              onClick={() => navigate(`/security/events?actor=${encodeURIComponent(s.username!)}`)}
                              title={`보안 로그에서 ${s.username}의 이력 보기`}
                            >
                              <History size={12} /> 활동 상세
                            </button>
                          )}
                        </td>
                      </tr>
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
